import { readFile } from "node:fs/promises";
import type { MemoryStore } from "./store.js";

/**
 * Difference between two memory stores. All three lists may be empty,
 * which means the stores are in sync.
 */
export interface SyncDiff {
  /** Ids present in `a` but not in `b`. */
  onlyInA: readonly string[];
  /** Ids present in `b` but not in `a`. */
  onlyInB: readonly string[];
  /** Ids present in both but whose raw file contents differ. */
  changed: readonly string[];
}

/**
 * Compute the diff between two memory stores. Neither store is modified.
 *
 * `changed` is determined by exact byte comparison of the underlying
 * `.md` files. Frontmatter ordering or whitespace differences therefore
 * count as a change — callers that want semantic equality should
 * re-serialize through `parseFrontmatter` / `serializeFrontmatter` before
 * comparing.
 */
export async function diffStores(
  a: MemoryStore,
  b: MemoryStore,
): Promise<SyncDiff> {
  const [idsA, idsB] = await Promise.all([a.list(), b.list()]);
  const setA = new Set(idsA);
  const setB = new Set(idsB);

  const onlyInA = idsA.filter((id) => !setB.has(id));
  const onlyInB = idsB.filter((id) => !setA.has(id));
  const both = idsA.filter((id) => setB.has(id));

  const changed: string[] = [];
  for (const id of both) {
    const [contentA, contentB] = await Promise.all([
      readFile(a.pathFor(id), "utf8"),
      readFile(b.pathFor(id), "utf8"),
    ]);
    if (contentA !== contentB) {
      changed.push(id);
    }
  }

  return { onlyInA, onlyInB, changed };
}
